import { Redirect, Route, Switch, useLocation } from 'react-router'
import { SvgIconComponent } from '@mui/icons-material'
import { paths } from 'config'
import { ModuleRoute } from 'shared/types'
import { useDialog, useIsSmaller, useSessionGuard } from 'shared/hooks'
import { PrivateRoute } from '..'
import Topbar from './Topbar'
import Drawer from './Drawer'
import {
  useCurrentLayoutSettings,
  useCurrentRoute,
  useAvailableRoutes,
} from './Layout.utils'
import { Container, Content } from './Layout.style'
import SecuritySnackbars from '../SecuritySnackbars'
import PageLoader from '../PageLoader'

export interface LayoutNavItem {
  path: string
  label: string
  icon?: SvgIconComponent
}

export interface LayoutProps {
  routes: ModuleRoute[]
  hideDrawer?: boolean
  hideTopbar?: boolean
}

const Layout = ({ routes }: LayoutProps) => {
  const { search } = useLocation()
  const {
    drawerProps = {},
    topbarProps = {},
    contentProps = {},
  } = useCurrentLayoutSettings(routes)
  // Sign-in and other public pages are shown before the user is signed in
  const isPublicRoute = !!useCurrentRoute(routes)?.public
  const isSmaller = useIsSmaller('xl')
  const [drawerOpen, toggleDrawerOpen] = useDialog()
  useSessionGuard()

  const { availableRoutes, isUserDataPending } = useAvailableRoutes(routes)

  const getNavItems = (availableRoutes: ModuleRoute[]) =>
    availableRoutes.filter(
      ({ linkSettings: { label, icon } = {} }) => label || icon
    )

  return (
    <Container>
      <Topbar {...topbarProps} onMenuClick={toggleDrawerOpen} />
      {!isPublicRoute && <SecuritySnackbars />}
      <Content
        data-scroll-container
        shrink={isSmaller ? false : !drawerProps.hidden}
        {...contentProps}
      >
        <Drawer
          navItems={getNavItems(availableRoutes)}
          {...drawerProps}
          hidden={drawerProps.hidden}
          open={drawerOpen}
          onClose={isSmaller ? toggleDrawerOpen : undefined}
          variant={isSmaller ? 'temporary' : 'permanent'}
        />
        <Switch>
          {availableRoutes
            .reduce(
              (prev, { subroutes, ...route }) =>
                subroutes ? [...prev, route, ...subroutes] : [...prev, route],
              [] as ModuleRoute[]
            )
            .map(route => (
              <PrivateRoute key={route.path} {...route} />
            ))}
          <Route
            component={() =>
              isUserDataPending ? (
                <PageLoader />
              ) : (
                <Redirect to={`${paths.signIn}${search}`} />
              )
            }
          />
        </Switch>
      </Content>
    </Container>
  )
}

export default Layout
